import React, { useEffect, useState } from "react";
import ResourcesPic from "./resources.jpg";

// ✅ Base URLs
const API_URL = "https://portal.lsb.go.ke/lsb";
const MEDIA_URL = "https://portal.lsb.go.ke";

// ✅ Helper: resolve category image from API
function getCategoryImageUrl(category) {
  const imgPath = category?.document;
  if (typeof imgPath === "string" && imgPath.trim() !== "") {
    if (imgPath.startsWith("http://") || imgPath.startsWith("https://")) {
      return imgPath;
    }
    const normalizedPath = imgPath.startsWith("/") ? imgPath : `/media/Category/${imgPath}`;
    return `${MEDIA_URL}${normalizedPath}`;
  }
  return ResourcesPic;
}

function Resources() {
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Fetch categories & resources
  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`${API_URL}/category/`).then((res) => res.json()),
      fetch(`${API_URL}/resources/`).then((res) => res.json()),
    ])
      .then(([categoriesData, resourcesData]) => {
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setResources(Array.isArray(resourcesData) ? resourcesData : []);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch resources.");
        setLoading(false);
      });
  }, []);

  // ✅ Group resources by category ID or name
  const resourcesByCategory = resources.reduce((acc, res) => {
    const key = res.category || res.type_resource || "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(res);
    return acc;
  }, {});

  return (
    <div className="mb-3">
      <div className="relative mb-12">
        <img
          className="h-96 w-full object-cover object-center rounded-b-2xl shadow-2xl"
          src={ResourcesPic}
          alt="A beautiful landscape"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-b-2xl"></div>
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center w-full px-4">
          <h1
            className="text-4xl md:text-5xl font-extrabold text-white mb-3 text-center tracking-tight shadow-sm"
          >
            Important Resources
          </h1>
        </div>
      </div>

      <div className="px-8 lg:px-20 my-10">
        <p className="text-center mb-6">
          Get access to all the publicly declared publications and print material produced by the LSB, including past exam papers, forms, publications, and relevant legislation.
        </p>

        {loading && (
          <div className="text-center my-5 text-gray-600">
            Loading resources...
          </div>
        )}
        {error && (
          <div className="text-center my-5 text-red-500">{error}</div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
          {categories.map((category) => {
            const relatedResources =
              resourcesByCategory[category.id] ||
              resourcesByCategory[category.name] ||
              [];

            // Each category card must use its unique image from API
            const categoryImgUrl = getCategoryImageUrl(category);

            return (
              <section key={category.id || category.name}>
                {/* ✅ Category Card */}
                <div className="relative overflow-hidden rounded-t-lg card-container">
                  <img
                    alt={category.name}
                    src={categoryImgUrl}
                    className="absolute inset-0 w-full h-full object-cover card-image"
                    style={{ minHeight: "250px" }}
                    onError={(e) => { e.target.onerror = null; e.target.src = ResourcesPic; }}
                  />

                  <div className="relative bg-gradient-to-t from-gray-900/70 to-gray-900/20 pt-32 sm:pt-48 lg:pt-64 card-content">
                    <div className="p-3">
                      <h5 className="text-white uppercase font-bold">
                        {category.name}
                      </h5>

                      <p className="text-white font-light">{category.description}</p>
                    </div>
                  </div>
                </div>

                {/* ✅ Resource Dropdown */}
                {relatedResources.length > 0 && (
                  <details
                    className="group [&_summary::-webkit-details-marker]:hidden"
                    closed
                  >
                    <summary className="flex cursor-pointer items-center justify-between gap-1.5 rounded-b-lg bg-gray-300 px-3 py-2.5 text-gray-900 font-medium">
                      {category.name}{" "}
                      <i className="fa-solid fa-chevron-down size-5 shrink-0 transition duration-300 group-open:-rotate-180"></i>
                    </summary>

                    <div className="py-2 flex justify-center gap-2 flex-wrap px-3">
                      {relatedResources.map((resource) => {
                        const documentUrl = resource.document
                          ? `${MEDIA_URL}${resource.document.startsWith("/media/") ? resource.document : `/media/Resource/${resource.document}`}`
                          : "#";

                        return (
                          <a
                            key={resource.resource_id}
                            href={documentUrl}
                            rel="noreferrer"
                            target="_blank"
                            download
                            className="text-decoration-none text-black border-2 border-gray-400 px-2 py-1 hover:border-[#DAA520] text-sm"
                          >
                            {resource.title || "Untitled Resource"}
                          </a>
                        );
                      })}
                    </div>
                  </details>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Resources;
